import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  Node,
  getBezierPath,
  useNodes,
} from "@xyflow/react";
import { Outcome } from "@unlikelyai-magic/shared/data-access/api";
import { cn } from "@unlikelyai-magic/utils";
import { RFNodeData } from "../utils";

const valueLabel = (value: Outcome): string => {
  switch (value) {
    case Outcome.TRUE:
      return "Yes";
    case Outcome.FALSE:
      return "No";
    case Outcome.UNKNOWN:
      return "Don't Know";
    case Outcome.UNDECIDED:
    default:
      return "Undecided";
  }
};

export const CustomEdge = ({
  id,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
}: EdgeProps) => {
  const nodes = useNodes<Node<RFNodeData>>();
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });
  const targetNode: Node<RFNodeData> | undefined = nodes.find(
    (node) => node.id === target
  );
  const outcome: Outcome | undefined = targetNode?.data.value;
  const isUndecided: boolean = outcome === Outcome.UNDECIDED;

  // Read propagateWhen from the target node's data
  const rawPropagateWhen = targetNode?.data.propagateWhen as
    | Outcome[]
    | string[]
    | undefined;
  const propagateWhen: Outcome[] | undefined = Array.isArray(rawPropagateWhen)
    ? (rawPropagateWhen.map((v) =>
        typeof v === "string" ? ((Outcome as any)[v] ?? v) : v
      ) as Outcome[])
    : undefined;
  const hasPropagateWhen = Boolean(propagateWhen && propagateWhen.length);

  // Does the current child outcome satisfy propagate_when?
  const countsNow: boolean =
    !hasPropagateWhen ||
    (Boolean(outcome) && Boolean(propagateWhen?.includes(outcome as Outcome)));

  const outcomeText: string =
    outcome === Outcome.TRUE
      ? "Yes"
      : outcome === Outcome.FALSE
        ? "No"
        : outcome === Outcome.UNKNOWN
          ? "Don't Know"
          : "";
  const backgroundColor: string =
    outcome === Outcome.TRUE
      ? "bg-green-500"
      : outcome === Outcome.FALSE
        ? "bg-red-500"
        : outcome === Outcome.UNKNOWN
          ? "bg-orange-500"
          : "bg-white";
  const borderColor: string =
    outcome === Outcome.TRUE
      ? "border-green-500"
      : outcome === Outcome.FALSE
        ? "border-red-500"
        : outcome === Outcome.UNKNOWN
          ? "border-orange-500"
          : "border-gray-200";
  const strokeColor: string =
    outcome === Outcome.TRUE
      ? "stroke-green-500"
      : outcome === Outcome.FALSE
        ? "stroke-red-500"
        : "";

  // Build a label describing the propagate_when condition
  const propagateWhenText: string | null =
    hasPropagateWhen && propagateWhen
      ? `Counts when: ${propagateWhen.map(valueLabel).join(" / ")}`
      : null;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          strokeWidth: 2,
          strokeDasharray: "6 3",
          // Fade the edge when the child's current outcome does not satisfy propagate_when
          opacity: countsNow ? 1 : 0.35,
        }}
        markerEnd={markerEnd}
        className={strokeColor}
      />
      {outcome && outcome !== Outcome.UNDECIDED && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: "all",
            }}
            className={cn(
              "z-10 p-3 py-1 rounded-lg flex flex-col items-center justify-center font-semibold nodrag nopan",
              borderColor,
              backgroundColor,
              !isUndecided && "text-white"
            )}
          >
            <span>{outcomeText}</span>
            {propagateWhenText && (
              <span className="mt-0.5 text-[10px] leading-tight text-white/80">
                {propagateWhenText}
              </span>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
      {/* Show propagate_when hint even when undecided */}
      {isUndecided && hasPropagateWhen && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: "all",
            }}
            className="z-10 px-2 py-1 rounded-lg bg-zinc-100 border border-zinc-300 text-[10px] text-zinc-500 nodrag nopan"
          >
            {propagateWhenText}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};
